import { DateTime } from "luxon";

export interface DateFormatOptions {
  inputZone?: string;
  outputZone?: string;
}

/**
 * Prints a date in a medium format. "Jan 1, 2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintDateMed(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "Invalid DateTime";
}

/**
 * Prints a date in a month and year format. "Jan 2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintMonthYear(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid
    ? dt.toLocaleString({ month: "short", year: "numeric" })
    : "Invalid DateTime";
}

export function prettyPrintDateTimeMed(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const { inputZone = "utc", outputZone } = options || {};
  let dt = DateTime.fromISO(isoDateStr, { zone: inputZone });
  if (outputZone !== undefined) {
    dt = dt.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dt = dt.setZone();
  }
  return dt.isValid
    ? dt.toLocaleString(DateTime.DATETIME_MED)
    : "Invalid DateTime";
}

/**
 * Prints a date in a full format. "January 1, 2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintDateFull(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid
    ? dt.toLocaleString(DateTime.DATE_FULL)
    : "Invalid DateTime";
}

/**
 * Prints a date in a short format. "01/01/2022"
 *
 * @param isoDateStr - The date string to print.
 * @param options - The options for the date format.
 * @returns The formatted date string.
 */
export function prettyPrintDate(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const dt = getDateFromString(isoDateStr, options);
  return dt.isValid ? dt.toFormat("LL/dd/yyyy") : "Invalid DateTime";
}

export function prettyPrintDateTime(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const { inputZone = "utc", outputZone } = options || {};
  let dt = DateTime.fromISO(isoDateStr, { zone: inputZone });
  if (outputZone !== undefined) {
    dt = dt.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dt = dt.setZone();
  }
  return dt.isValid ? dt.toFormat("MM/dd/yyyy hh:mm a") : "Invalid DateTime";
}

export function prettyPrintDateTimeWithSeconds(
  isoDateStr: string,
  options?: DateFormatOptions
) {
  const { inputZone = "utc", outputZone } = options || {};
  let dt = DateTime.fromISO(isoDateStr, { zone: inputZone });
  if (outputZone !== undefined) {
    dt = dt.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dt = dt.setZone();
  }
  return dt.isValid ? dt.toFormat("MM/dd/yyyy hh:mm:ss a") : "Invalid DateTime";
}

/**
 * Relative time is useful for logs, but after about a week it's better to see
 * the full date time.
 * After relativeCutoffHours, the date will be formatted as a full date -- defaults to a week.
 */
export function relativeLogTime(
  isoDateStr: string,
  relativeCutoffHours = 24 * 7,
  options?: DateFormatOptions
) {
  const { outputZone } = options || {};
  let dateTime = DateTime.fromISO(isoDateStr);
  if (outputZone !== undefined) {
    dateTime = dateTime.setZone(outputZone);
  } else {
    // Convert to local timezone when outputZone is not specified
    dateTime = dateTime.setZone();
  }
  const now =
    outputZone !== undefined
      ? DateTime.now().setZone(outputZone)
      : DateTime.now();
  const relativeCutoffMoment = now.minus({ hours: relativeCutoffHours });

  // Check if the date is more than a year ago
  if (dateTime < relativeCutoffMoment) {
    // Format it in a specific way, e.g., 'MM/dd/yyyy'
    return prettyPrintDateTime(isoDateStr, { outputZone });
  } else {
    // Use relative formatting
    return dateTime.toRelative({ base: now }) || "";
  }
}

/**
 * Gets a date from a string.
 *
 * If the string does not contain a timezone (e.g. "2022-01-01"), it will be
 * considered UTC and NOT be converted to a local timezone.
 *
 * If the string contains a timezone, it will be used and converted to the
 * output zone (local timezone if not specified).
 *
 * @param options
 * @param isoDateStr
 * @returns
 */
function getDateFromString(
  isoDateStr: string,
  options: DateFormatOptions | undefined
) {
  const { inputZone, outputZone } = options || {};
  const dt = DateTime.fromISO(isoDateStr, { zone: inputZone }).setZone(
    outputZone
  );
  return dt;
}
